import { SystemUserRole } from '@inshuwa-client/models';
import { baseUrl } from '../configs/baseUrl';

/**
 * Storage for "who this tab is signed in as".
 *
 * By default it is plain localStorage, shared by every tab, as it always was. A portal that calls
 * enablePerTabSession() (the partner portal: one person can represent several partners on one broker's
 * domain) keeps it in sessionStorage instead, so each tab holds its own account. localStorage then only
 * holds the copy of the most recently focused signed-in tab, used to start new tabs as that account.
 */
const SESSION_KEYS = [
  'token',
  'permissions',
  SystemUserRole.PartnerRepresentative,
  'partner',
  'Partner',
  'branchId',
];
// Per tab only: an impersonation session never becomes the copy new tabs start from.
const IMPERSONATION_KEY = 'impersonationSession';
const IMPERSONATION_HASH_PARAM = 'icImpersonationToken';
// Set when this tab could not get its own session from the shared copy, so a reload doesn't retry forever.
const SEED_FAILED_KEY = 'ic-session-seed-failed';

let perTab = false;
let channel: BroadcastChannel | null = null;

type SessionMessage =
  | { type: 'hello'; refreshToken: string }
  | { type: 'mine'; refreshToken: string }
  | { type: 'logout' };

const isImpersonationTab = () =>
  sessionStorage.getItem(IMPERSONATION_KEY) !== null;

const mayWriteSharedCopy = () => !document.hidden && !isImpersonationTab();

const tabRefreshToken = (): string | null => {
  const raw = sessionStorage.getItem('token');
  return raw ? JSON.parse(raw).refreshToken ?? null : null;
};

// The refresh endpoints identify the session by user id + refresh token. The id is read from the access
// token without verifying it, which is fine here: the server checks the pair, and it still works once the
// access token has expired.
const refreshRequest = (raw: string | null) => {
  if (!raw) return null;
  try {
    const { accessToken, refreshToken } = JSON.parse(raw);
    const payload = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const userId = Number(JSON.parse(atob(payload)).id);
    return refreshToken && userId ? { userId, token: refreshToken } : null;
  } catch {
    return null;
  }
};

export const sessionStore = {
  getItem: (key: string): string | null =>
    (perTab ? sessionStorage : localStorage).getItem(key),

  setItem(key: string, value: string): void {
    if (!perTab) return localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
    // Background tabs (e.g. a silent token refresh) must not take over what new tabs open as.
    if (key !== IMPERSONATION_KEY && mayWriteSharedCopy()) {
      localStorage.setItem(key, value);
    }
  },

  removeItem(key: string): void {
    if (!perTab) return localStorage.removeItem(key);
    sessionStorage.removeItem(key);
    if (key !== IMPERSONATION_KEY && mayWriteSharedCopy()) {
      localStorage.removeItem(key);
    }
  },
};

export const isPerTabSession = () => perTab;

/** Call once, before bootstrap. */
export function enablePerTabSession(): void {
  perTab = true;

  const arrivingImpersonation = location.hash.includes(
    `${IMPERSONATION_HASH_PARAM}=`,
  );
  const seeded =
    !arrivingImpersonation &&
    sessionStorage.getItem('token') === null &&
    sessionStorage.getItem(SEED_FAILED_KEY) === null &&
    localStorage.getItem('token') !== null;

  if (seeded) {
    for (const key of SESSION_KEYS) {
      const value = localStorage.getItem(key);
      if (value !== null) sessionStorage.setItem(key, value);
    }
  }

  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel('ic-session');
    channel.onmessage = ({ data }: MessageEvent<SessionMessage>) => {
      const mine = tabRefreshToken();
      if (data.type === 'hello' && mine && data.refreshToken === mine) {
        channel?.postMessage({ type: 'mine', refreshToken: mine });
      } else if (data.type === 'mine' && mine && data.refreshToken === mine) {
        // Another live tab holds this refresh token: we're a duplicated tab.
        void forkSession();
      } else if (data.type === 'logout') {
        revokeTabSession();
        clearTabSession();
        location.assign('/');
      }
    };
  }

  if (seeded) {
    // Started from another tab's copy, so it shares that tab's single-use refresh token.
    void forkSession();
  } else {
    // "Duplicate tab" copies sessionStorage, refresh token included; ask whether another tab has it.
    const mine = tabRefreshToken();
    if (mine) channel?.postMessage({ type: 'hello', refreshToken: mine });
  }

  // New tabs open as the account of the tab the user was last looking at.
  const publish = () => {
    if (!mayWriteSharedCopy() || sessionStorage.getItem('token') === null) {
      return;
    }
    for (const key of SESSION_KEYS) {
      const value = sessionStorage.getItem(key);
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    }
  };
  document.addEventListener('visibilitychange', publish);
  window.addEventListener('focus', publish);
}

let forking = false;

// Swap the shared refresh token for one of this tab's own (the backend issues it without consuming the
// current one, so the other tab keeps working). It works even if the copied access token has expired.
// If it fails the tab goes back to login rather than racing the other tab for the token.
async function forkSession(): Promise<void> {
  const before = sessionStorage.getItem('token');
  if (forking || !before) return;
  forking = true;

  try {
    const request = refreshRequest(before);
    if (!request) throw new Error('no refresh token to fork');
    const response = await fetch(`${baseUrl()}/auth/refresh-token/fork`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`fork failed: ${response.status}`);
    const token = await response.json();
    // The tab may have signed in as someone else meanwhile (e.g. impersonation); keep that.
    if (sessionStorage.getItem('token') === before) {
      sessionStorage.setItem(
        'token',
        JSON.stringify({
          accessToken: token.accessToken,
          refreshToken: token.refreshToken,
        }),
      );
    }
  } catch {
    if (sessionStorage.getItem('token') === before) {
      clearTabSession();
      sessionStorage.setItem(SEED_FAILED_KEY, '1');
      location.assign('/');
    }
  } finally {
    forking = false;
  }
}

// Logout ends the session server-side too, so its refresh token can't be redeemed afterwards. keepalive lets
// the request outlive the reload that follows logout.
function revokeTabSession(): void {
  const request = refreshRequest(
    (perTab ? sessionStorage : localStorage).getItem('token'),
  );
  if (!request) return;
  void fetch(`${baseUrl()}/auth/logout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    keepalive: true,
  }).catch(() => undefined);
}

function clearTabSession(): void {
  for (const key of [...SESSION_KEYS, IMPERSONATION_KEY]) {
    sessionStorage.removeItem(key);
  }
}

/**
 * Storage side of logging out. everywhere=false is for a tab whose own session died (refresh failed);
 * it must not sign out the other tabs, which may be different accounts.
 */
export function endSession(everywhere: boolean): void {
  revokeTabSession();
  if (!perTab || everywhere) {
    const brokerId = localStorage.getItem('brokerId');
    localStorage.clear();
    if (brokerId) localStorage.setItem('brokerId', brokerId);
  }
  if (perTab) {
    clearTabSession();
    sessionStorage.removeItem(SEED_FAILED_KEY);
    if (everywhere) channel?.postMessage({ type: 'logout' });
  }
}
